/**
 * Scenario helpers: which input cells differ from the market consensus, and a
 * deterministic short hash that identifies a scenario.
 *
 * The hash is an identifier, not a proof of reproducibility: it is only a
 * reproducibility key when the backend can retrieve the model artefacts named
 * by `modelVersion` (see docs/forecast-api-v2.md).
 */

/** Numeric equality tolerant of the 2-decimal string convention. */
export function sameValue(a, b) {
  if (a === b) return true;
  const x = Number(a);
  const y = Number(b);
  if (Number.isFinite(x) && Number.isFinite(y)) return Math.abs(x - y) < 1e-9;
  return String(a ?? "") === String(b ?? "");
}

/**
 * List the cells that differ between the edited rows and the original
 * consensus rows. Header rows are ignored. Each entry carries the row and
 * column indices plus the original and current values.
 */
export function changedCells(rows, originals, quarters = []) {
  const out = [];
  if (!rows || !originals) return out;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const orig = originals[i];
    if (!row || row.header || !orig) continue;
    for (let j = 0; j < row.data.length; j++) {
      if (!sameValue(row.data[j], orig.data[j])) {
        out.push({
          rowIndex: i,
          colIndex: j,
          name: row.name,
          quarter: quarters[j],
          from: orig.data[j],
          to: row.data[j],
        });
      }
    }
  }
  return out;
}

/** Number of distinct input rows with at least one modified cell. */
export function changedInputCount(rows, originals) {
  const names = new Set(changedCells(rows, originals).map((c) => c.rowIndex));
  return names.size;
}

/** Scenario label used in the results header. */
export function scenarioLabel(rows, originals) {
  const n = changedInputCount(rows, originals);
  if (n === 0) return "Scenario: Consensus";
  return `Scenario: Alternative (${n} input${n === 1 ? "" : "s"} changed)`;
}

/**
 * Canonical JSON: object keys sorted recursively, numbers normalised through
 * `Number()` so "2.02" and 2.02 hash the same.
 */
export function canonicalJson(value) {
  if (Array.isArray(value)) return "[" + value.map(canonicalJson).join(",") + "]";
  if (value && typeof value === "object") {
    const keys = Object.keys(value).sort();
    return "{" + keys.map((k) => JSON.stringify(k) + ":" + canonicalJson(value[k])).join(",") + "}";
  }
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return String(Number(value));
  }
  return JSON.stringify(value);
}

/** FNV-1a 32-bit hash of a string, as 8 lowercase hex characters. */
export function fnv1a32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/**
 * Deterministic scenario hash from the actual inputs, the country, the SPX
 * mode and the model version string (may be null when unknown).
 */
export function scenarioHash({ country, endogenous, quarters, rows, modelVersion = null }) {
  const inputs = {};
  for (const row of rows || []) {
    if (row.header) continue;
    inputs[row.name] = {};
    row.data.forEach((v, j) => {
      inputs[row.name][quarters[j] ?? String(j)] = v;
    });
  }
  return fnv1a32(canonicalJson({ country, endogenous: !!endogenous, inputs, modelVersion }));
}
